import { useEffect, useRef } from "react";

/** The six-box authenticator code, with the behaviour people expect from one.
 *
 *  EXTRACTED, NOT WRITTEN TWICE. `LoginPage` grew this first; MFA *enrolment*
 *  needs exactly the same control, and a second hand-built copy is how the two
 *  drift — one gets paste support, the other does not.
 *
 *  `onComplete` fires when the sixth digit lands, by typing OR by paste. The
 *  caller is responsible for not sending the same code twice: Fabric burns a
 *  TOTP timestep on first use (replay guard), so a resend comes back
 *  MFA_INVALID and counts against the throttle.
 */
export function OtpInput({ value, onChange, onComplete, autoFocus = false, disabled = false }: {
  value: string[];
  onChange: (next: string[]) => void;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) boxes.current[0]?.focus();
  }, [autoFocus]);

  function set(i: number, v: string) {
    const digit = v.replace(/\D/g, "").slice(-1);
    const next = [...value];
    next[i] = digit;
    onChange(next);
    if (digit && i < 5) boxes.current[i + 1]?.focus();
    if (next.every((d) => d)) onComplete?.(next.join(""));
  }

  function onKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !value[i] && i > 0) boxes.current[i - 1]?.focus();
  }

  function onPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const next = ["", "", "", "", "", ""].map((_, i) => digits[i] ?? "");
    onChange(next);
    if (digits.length === 6) onComplete?.(next.join(""));
    else boxes.current[Math.min(digits.length, 5)]?.focus();
  }

  return (
    <div className="otp-grid" onPaste={onPaste}>
      {value.map((d, i) => (
        <input
          key={i}
          ref={(el) => (boxes.current[i] = el)}
          className="otp-box"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          autoComplete={i === 0 ? "one-time-code" : undefined}
          onChange={(e) => set(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
        />
      ))}
    </div>
  );
}

export const EMPTY_OTP = ["", "", "", "", "", ""];
